import { Category } from '../types';
import { BarChart4, FileCheck2, FileText, Globe, LayoutPanelTop as LayoutPlaneLine, Package, Smartphone, Truck, Scale, ShieldAlert } from 'lucide-react';

// Helper function to create yes/no questions
const createYesNoQuestion = (id: string, text: string) => ({
  id,
  text,
  type: 'yes-no' as const,
  options: [
    { id: `${id}-1`, text: 'No', value: 0 },
    { id: `${id}-2`, text: 'Yes', value: 5 }
  ]
});

// Helper function to create Likert scale questions
const createLikertQuestion = (id: string, text: string) => ({
  id,
  text,
  type: 'likert' as const,
  options: [
    { id: `${id}-1`, text: '1 - Not at all', value: 1 },
    { id: `${id}-2`, text: '2 - Limited extent', value: 2 },
    { id: `${id}-3`, text: '3 - Moderate extent', value: 3 },
    { id: `${id}-4`, text: '4 - Large extent', value: 4 },
    { id: `${id}-5`, text: '5 - Completely', value: 5 }
  ]
});

// Helper function to create multiple choice questions
const createMultipleChoiceQuestion = (id: string, text: string, options: Array<{ text: string; value: number }>) => ({
  id,
  text,
  type: 'multiple-choice' as const,
  options: options.map((opt, index) => ({
    id: `${id}-${index + 1}`,
    text: opt.text,
    value: opt.value
  }))
});

// Business Plan Questions
const businessPlanQuestions = [
  createYesNoQuestion('business-plan-q1', 'Do you have a documented export business plan?'),
  createLikertQuestion('business-plan-q2', 'How would you rate your company\'s readiness to allocate resources for export activities?'),
  createMultipleChoiceQuestion('business-plan-q3', 'Which of the following best describes your export expansion timeline?', [
    { text: 'No timeline established', value: 1 },
    { text: 'Planning to start within 2 years', value: 2 },
    { text: 'Planning to start within 1 year', value: 3 },
    { text: 'Planning to start within 6 months', value: 4 },
    { text: 'Already exporting or ready to start immediately', value: 5 }
  ]),
  createYesNoQuestion('business-plan-q4', 'Has your company identified specific target markets for export?'),
  createYesNoQuestion('business-plan-q5', 'Do you have dedicated staff for managing export operations?'),
  createLikertQuestion('business-plan-q6', 'How comprehensive is your understanding of the export supply chain for your products?'),
  createYesNoQuestion('business-plan-q7', 'Does your business plan include a budget for export marketing activities?'),
  createLikertQuestion('business-plan-q8', 'How confident are you in your pricing strategy for international markets?'),
  createYesNoQuestion('business-plan-q9', 'Do you have a clear value proposition for international customers?'),
  createYesNoQuestion('business-plan-q10', 'Have you conducted a SWOT analysis focused on export potential?'),
  createLikertQuestion('business-plan-q11', 'How developed is your internal team\'s understanding of international trade regulations?'),
  createYesNoQuestion('business-plan-q12', 'Do you have clearly defined export goals and KPIs?'),
  createLikertQuestion('business-plan-q13', 'How well does your business plan address risks related to exporting?'),
  createYesNoQuestion('business-plan-q14', 'Do you have contingency plans in case of export disruptions?'),
  createYesNoQuestion('business-plan-q15', 'Is there a clearly defined person or team responsible for implementing the export plan?'),
  createLikertQuestion('business-plan-q16', 'How aligned is your export plan with your company\'s overall growth strategy?'),
  createYesNoQuestion('business-plan-q17', 'Does your export plan consider legal and regulatory compliance in target countries?'),
  createYesNoQuestion('business-plan-q18', 'Do you have plans for after-sales service or support for international clients?'),
  createMultipleChoiceQuestion('business-plan-q19', 'How frequently is your export business plan reviewed and updated?', [
    { text: 'Never', value: 1 },
    { text: 'Annually', value: 2 },
    { text: 'Semi-annually', value: 3 },
    { text: 'Quarterly', value: 4 },
    { text: 'Monthly or more frequently', value: 5 }
  ]),
  createYesNoQuestion('business-plan-q20', 'Do you have performance tracking systems in place for your export operations?')
];

// Export Finance Questions
const financeQuestions = [
  createYesNoQuestion('finance-q1', 'Do you have a dedicated budget for export-related expenses?'),
  createLikertQuestion('finance-q2', 'How confident are you in your pricing strategy to remain competitive internationally?'),
  createYesNoQuestion('finance-q3', 'Have you analyzed all costs involved in getting your product to the international customer (e.g., shipping, tariffs, insurance)?'),
  createMultipleChoiceQuestion('finance-q4', 'Which best describes how you currently fund your export activities?', [
    { text: 'No funding available', value: 1 },
    { text: 'Internal funds only', value: 2 },
    { text: 'Bank loans', value: 3 },
    { text: 'Trade finance facilities', value: 4 },
    { text: 'Multiple funding sources', value: 5 }
  ]),
  createYesNoQuestion('finance-q5', 'Do you have a financial forecasting system for international sales?'),
  createMultipleChoiceQuestion('finance-q6', 'How often do you review your export profitability margins?', [
    { text: 'Never', value: 1 },
    { text: 'Annually', value: 2 },
    { text: 'Quarterly', value: 3 },
    { text: 'Monthly', value: 4 },
    { text: 'Weekly or more frequently', value: 5 }
  ]),
  createYesNoQuestion('finance-q7', 'Have you established credit terms with your international buyers?'),
  createLikertQuestion('finance-q8', 'Do you have experience managing foreign exchange risks?'),
  createMultipleChoiceQuestion('finance-q9', 'Which of the following best describes your payment collection strategy for exports?', [
    { text: 'No strategy in place', value: 1 },
    { text: 'Basic payment terms only', value: 2 },
    { text: 'Letters of credit', value: 3 },
    { text: 'Multiple payment methods', value: 4 },
    { text: 'Comprehensive strategy with multiple options', value: 5 }
  ]),
  createYesNoQuestion('finance-q10', 'Do you know how to access trade finance instruments like invoice factoring or forfaiting?'),
  createLikertQuestion('finance-q11', 'How would you rate your company\'s access to export finance or trade credit facilities?'),
  createYesNoQuestion('finance-q12', 'Have you explored government-backed export financing options (e.g., EXIM Bank, NEXIM)?'),
  createYesNoQuestion('finance-q13', 'Do you include duties, taxes, and compliance costs in your export pricing?'),
  createYesNoQuestion('finance-q14', 'Have you prepared for delayed payments or default scenarios from international buyers?'),
  createLikertQuestion('finance-q15', 'How diversified is your customer base to reduce financial risk from any one market?'),
  createYesNoQuestion('finance-q16', 'Do you track your export cash flow separately from domestic operations?'),
  createYesNoQuestion('finance-q17', 'Do you use export costing tools or software to manage financial planning?'),
  createLikertQuestion('finance-q18', 'How prepared is your company to absorb currency fluctuations in export markets?'),
  createYesNoQuestion('finance-q19', 'Do you monitor and plan for financing needs as export volumes grow?'),
  createYesNoQuestion('finance-q20', 'Have you sought professional financial advice specifically related to international trade?')
];

// Export Documentation Questions
const documentationQuestions = [
  createYesNoQuestion('documentation-q1', 'Do you have a standardized process for preparing export documentation?'),
  createLikertQuestion('documentation-q2', 'How familiar are you with the documents required for exporting from your country?'),
  createYesNoQuestion('documentation-q3', 'Do you know how to prepare a commercial invoice for export?'),
  createMultipleChoiceQuestion('documentation-q4', 'Which export documents have you previously prepared?', [
    { text: 'None', value: 1 },
    { text: 'Basic shipping documents only', value: 2 },
    { text: 'Most common export documents', value: 3 },
    { text: 'All standard export documents', value: 4 },
    { text: 'All documents including specialized certificates', value: 5 }
  ]),
  createYesNoQuestion('documentation-q5', 'Do you have templates or tools to create accurate export documentation?'),
  createLikertQuestion('documentation-q6', 'How confident are you in completing export customs declarations?'),
  createYesNoQuestion('documentation-q7', 'Do you maintain a checklist of required documents for different export destinations?'),
  createYesNoQuestion('documentation-q8', 'Are you aware of country-specific documentation requirements (e.g., legalized invoices, consular invoices)?'),
  createYesNoQuestion('documentation-q9', 'Have you registered with the appropriate export clearance or customs authority?'),
  createMultipleChoiceQuestion('documentation-q10', 'Which Incoterms do you use when preparing export documentation?', [
    { text: 'Not familiar with Incoterms', value: 1 },
    { text: 'Basic understanding only', value: 2 },
    { text: 'Use common terms (EXW, FOB, CIF)', value: 3 },
    { text: 'Use multiple terms as needed', value: 4 },
    { text: 'Expert in all Incoterms', value: 5 }
  ]),
  createYesNoQuestion('documentation-q11', 'Do you retain export documentation for audit or compliance purposes?'),
  createYesNoQuestion('documentation-q12', 'Do you assign staff to review and verify export documentation before shipping?'),
  createYesNoQuestion('documentation-q13', 'Have you ever had a shipment delayed due to incorrect or incomplete documentation?'),
  createYesNoQuestion('documentation-q14', 'Do you use software or an ERP system to generate export documents automatically?'),
  createYesNoQuestion('documentation-q15', 'Are you familiar with the HS codes (Harmonized System) for your products?'),
  createYesNoQuestion('documentation-q16', 'Have you been trained or attended a workshop on export documentation?'),
  createYesNoQuestion('documentation-q17', 'Do you maintain both soft and hard copies of your export documentation?'),
  createLikertQuestion('documentation-q18', 'How quickly can you produce a complete set of export documents for a shipment?'),
  createYesNoQuestion('documentation-q19', 'Do you know how to obtain a certificate of conformity or phytosanitary certificate if required?'),
  createYesNoQuestion('documentation-q20', 'Do you confirm with the buyer or freight forwarder which documents are needed before shipment?')
];

// Product Readiness Questions
const productQuestions = [
  createYesNoQuestion('product-q1', 'Is your product currently being sold successfully in your domestic market?'),
  createYesNoQuestion('product-q2', 'Do you have enough production capacity to fulfill both domestic and international orders?'),
  createYesNoQuestion('product-q3', 'Has your product been tested for durability or performance in international conditions (e.g., shipping, climate)?'),
  createYesNoQuestion('product-q4', 'Do your products meet international quality or safety standards (e.g., CE, ISO, FDA)?'),
  createYesNoQuestion('product-q5', 'Have you conducted product labeling and packaging assessments for compliance with foreign market regulations?'),
  createYesNoQuestion('product-q6', 'Is your product packaging suitable for long-distance shipping and handling?'),
  createLikertQuestion('product-q7', 'How adaptable is your product to different cultural, legal, or consumer requirements in foreign markets?'),
  createYesNoQuestion('product-q8', 'Have you identified and documented your product\'s unique selling points (USPs) for export markets?'),
  createYesNoQuestion('product-q9', 'Do you have multi-language product manuals, labels, or instructions if required?'),
  createLikertQuestion('product-q10', 'Are your products priced competitively for foreign markets after including export-related costs?'),
  createYesNoQuestion('product-q11', 'Do you have product photos and digital assets suitable for international marketing?'),
  createYesNoQuestion('product-q12', 'Have you received positive customer reviews or feedback on your product\'s performance?'),
  createYesNoQuestion('product-q13', 'Is your product perishable or fragile, requiring special handling for export?'),
  createYesNoQuestion('product-q14', 'Can your product specifications (e.g., ingredients, voltage, dimensions) be adjusted for target markets?'),
  createYesNoQuestion('product-q15', 'Have you conducted a competitor analysis for your product in international markets?'),
  createYesNoQuestion('product-q16', 'Is your product classified as a restricted or controlled item in any target market?'),
  createYesNoQuestion('product-q17', 'Do you have an inventory management system to track production and export quantities?'),
  createYesNoQuestion('product-q18', 'Can your product withstand long transit times without loss of quality?'),
  createYesNoQuestion('product-q19', 'Is your product easily recognizable or differentiable from competitors?'),
  createYesNoQuestion('product-q20', 'Do you have supporting materials (e.g., brochures, certifications, test results) for your product?')
];

// Compliance Questions
const complianceQuestions = [
  createYesNoQuestion('compliance-q1', 'Are you aware of export control laws and regulations in your country?'),
  createYesNoQuestion('compliance-q2', 'Have you determined if your products require export licenses or permits?'),
  createYesNoQuestion('compliance-q3', 'Do you screen foreign buyers and partners against sanctions and restricted party lists?'),
  createYesNoQuestion('compliance-q4', 'Do you maintain records of all exports in accordance with legal requirements?'),
  createYesNoQuestion('compliance-q5', 'Are you aware of dual-use regulations that may apply to your product?'),
  createYesNoQuestion('compliance-q6', 'Do you comply with local labeling laws in export markets (e.g., language, safety symbols)?'),
  createYesNoQuestion('compliance-q7', 'Have you registered your product with any necessary foreign authorities (e.g., FDA, CE, ISO)?'),
  createYesNoQuestion('compliance-q8', 'Do you have documented compliance policies for international trade?'),
  createYesNoQuestion('compliance-q9', 'Are you familiar with the Harmonized System (HS) code for your product?'),
  createYesNoQuestion('compliance-q10', 'Do you know the required certificates for your product to enter specific export markets?'),
  createYesNoQuestion('compliance-q11', 'Are you aware of the ethical sourcing or sustainability certifications required in some countries?'),
  createYesNoQuestion('compliance-q12', 'Do you have a process for managing customer data in compliance with international privacy laws (e.g., GDPR)?'),
  createYesNoQuestion('compliance-q13', 'Have you reviewed contracts and trade terms (e.g., Incoterms) to ensure legal compliance?'),
  createYesNoQuestion('compliance-q14', 'Do you ensure that your product does not infringe on intellectual property rights in foreign markets?'),
  createYesNoQuestion('compliance-q15', 'Have you secured trademarks or patents for your products in your target export markets?'),
  createYesNoQuestion('compliance-q16', 'Do you monitor and update changes in export regulations that might impact your shipments?'),
  createYesNoQuestion('compliance-q17', 'Are your invoices, packing lists, and certificates of origin compliant with export requirements?'),
  createYesNoQuestion('compliance-q18', 'Do you have insurance to cover liability or compliance risks in foreign markets?'),
  createLikertQuestion('compliance-q19', 'How confident are you in your current level of export compliance?'),
  createYesNoQuestion('compliance-q20', 'Do you have a designated compliance officer or team handling export regulations?')
];

// Market Knowledge Questions
const marketQuestions = [
  createYesNoQuestion('market-q1', 'Have you identified specific countries or regions where you want to export your product?'),
  createYesNoQuestion('market-q2', 'Have you conducted any market research to understand demand for your product abroad?'),
  createYesNoQuestion('market-q3', 'Do you know who your competitors are in your target export markets?'),
  createLikertQuestion('market-q4', 'Do you understand the cultural preferences and consumer behavior of your target market?'),
  createLikertQuestion('market-q5', 'Do you have insights into current trends and market opportunities in your target countries?'),
  createYesNoQuestion('market-q6', 'Are you aware of the pricing expectations in your target export market?'),
  createLikertQuestion('market-q7', 'Do you understand the distribution channels used in your target markets?'),
  createYesNoQuestion('market-q8', 'Have you assessed the political and economic stability of your target markets?'),
  createLikertQuestion('market-q9', 'Are you familiar with customer expectations in terms of quality and service in the target market?'),
  createYesNoQuestion('market-q10', 'Do you have access to local contacts or partnerships in your target market?'),
  createLikertQuestion('market-q11', 'Do you know which marketing strategies are effective in your target countries?'),
  createYesNoQuestion('market-q12', 'Have you evaluated the demand size and market potential for your product abroad?'),
  createLikertQuestion('market-q13', 'Do you understand local regulations and standards that affect market entry?'),
  createYesNoQuestion('market-q14', 'Have you participated in international trade shows or virtual market discovery programs?'),
  createLikertQuestion('market-q15', 'Do you regularly monitor changes in market dynamics in your target countries?'),
  createLikertQuestion('market-q16', 'Do you know how your brand will be perceived in the target market?'),
  createYesNoQuestion('market-q17', 'Have you done a SWOT analysis for your product in the export market?'),
  createLikertQuestion('market-q18', 'Do you have a clear understanding of the customer journey in your target export market?'),
  createLikertQuestion('market-q19', 'Are you confident in your knowledge of market entry barriers such as tariffs or quotas?'),
  createYesNoQuestion('market-q20', 'Do you use any tools or services to keep up with market intelligence globally?')
];

// Digital Readiness Questions
const digitalQuestions = [
  createYesNoQuestion('digital-q1', 'Do you have a company website?'),
  createYesNoQuestion('digital-q2', 'Is your website optimized for international users (e.g., multi-language, multi-currency)?'),
  createYesNoQuestion('digital-q3', 'Do you have an e-commerce platform or online store for selling your products?'),
  createYesNoQuestion('digital-q4', 'Do you use Customer Relationship Management (CRM) software to manage clients and leads?'),
  createYesNoQuestion('digital-q5', 'Do you use digital marketing tools (SEO, Google Ads, email marketing, etc.)?'),
  createYesNoQuestion('digital-q6', 'Is your business active on international social media platforms (e.g., LinkedIn, Instagram, Facebook)?'),
  createYesNoQuestion('digital-q7', 'Do you use any online tools to manage your export documentation or shipping processes?'),
  createYesNoQuestion('digital-q8', 'Is your product or service discoverable online by international buyers?'),
  createYesNoQuestion('digital-q9', 'Do you have a digital catalog or product brochure you can send to international buyers?'),
  createYesNoQuestion('digital-q10', 'Is your IT infrastructure (e.g., email, cloud storage) secure and protected against cyber threats?'),
  createYesNoQuestion('digital-q11', 'Do you track analytics and user behavior on your website or online platforms?'),
  createLikertQuestion('digital-q12', 'Are your digital tools and software scalable as you expand into new markets?'),
  createYesNoQuestion('digital-q13', 'Can customers complete purchases online without direct contact with your sales team?'),
  createYesNoQuestion('digital-q14', 'Are your digital platforms mobile-friendly?'),
  createYesNoQuestion('digital-q15', 'Have you listed your products on international B2B or B2C marketplaces (e.g., Alibaba, Amazon, Etsy)?'),
  createLikertQuestion('digital-q16', 'Do you have internal IT or digital skills to support international growth?'),
  createYesNoQuestion('digital-q17', 'Do you have systems to automate order fulfillment and inventory management?'),
  createYesNoQuestion('digital-q18', 'Do you use technology to track customer feedback or reviews internationally?'),
  createYesNoQuestion('digital-q19', 'Are you prepared to use digital payment platforms for international transactions?'),
  createYesNoQuestion('digital-q20', 'Do you have a digital transformation roadmap to scale your export operations?')
];

// Export Logistics Questions
const logisticsQuestions = [
  createYesNoQuestion('logistics-q1', 'Do you have experience in exporting goods to international markets?'),
  createYesNoQuestion('logistics-q2', 'Do you have established relationships with reliable freight forwarders or logistics companies?'),
  createLikertQuestion('logistics-q3', 'Are you familiar with Incoterms and their implications on your export operations?'),
  createYesNoQuestion('logistics-q4', 'Do you know the cost and timeline for delivering your products to international markets?'),
  createYesNoQuestion('logistics-q5', 'Do you have processes for packaging goods according to international shipping standards?'),
  createYesNoQuestion('logistics-q6', 'Can your product be shipped by air, sea, and/or land depending on market needs?'),
  createYesNoQuestion('logistics-q7', 'Do you know how to obtain and complete a bill of lading or airway bill for exports?'),
  createYesNoQuestion('logistics-q8', 'Are you aware of the documentation required by customs authorities in your destination markets?'),
  createYesNoQuestion('logistics-q9', 'Do you have an internal team or partner who manages logistics and export documentation?'),
  createYesNoQuestion('logistics-q10', 'Are you able to track and trace shipments from origin to final destination?'),
  createLikertQuestion('logistics-q11', 'Do you understand the risks involved in exporting (e.g., delays, damage, losses)?'),
  createYesNoQuestion('logistics-q12', 'Do you insure your goods during international transportation?'),
  createYesNoQuestion('logistics-q13', 'Do you have a logistics plan that includes warehousing or consolidation services if needed?'),
  createYesNoQuestion('logistics-q14', 'Are your shipping and delivery terms clearly stated in buyer contracts or invoices?'),
  createYesNoQuestion('logistics-q15', 'Do you work with customs brokers or clearing agents in your export markets?'),
  createLikertQuestion('logistics-q16', 'Are you able to estimate and manage logistics costs for different markets accurately?'),
  createYesNoQuestion('logistics-q17', 'Do you conduct post-shipment reviews to evaluate logistics performance?'),
  createYesNoQuestion('logistics-q18', 'Are you prepared to comply with import regulations, packaging, and labeling requirements in the destination country?'),
  createLikertQuestion('logistics-q19', 'Do you regularly review and update your logistics and supply chain strategy?'),
  createYesNoQuestion('logistics-q20', 'Do you have backup logistics plans for emergencies such as port closures or transport strikes?')
];

// Legal & Regulatory Framework Questions
const legalQuestions = [
  createYesNoQuestion('legal-q1', 'Are you aware of the legal requirements for exporting goods from your country?'),
  createLikertQuestion('legal-q2', 'Do you understand the legal implications of international contracts and agreements?'),
  createYesNoQuestion('legal-q3', 'Do you have legal representation or access to legal advice for international trade matters?'),
  createLikertQuestion('legal-q4', 'Are you familiar with intellectual property rights (IPR) issues in your export destinations?'),
  createYesNoQuestion('legal-q5', 'Do you register trademarks or patents in foreign markets where you operate?'),
  createLikertQuestion('legal-q6', 'Do you understand the dispute resolution options available in international trade?'),
  createYesNoQuestion('legal-q7', 'Do you include governing law and jurisdiction clauses in your international contracts?'),
  createYesNoQuestion('legal-q8', 'Do you have terms and conditions drafted specifically for your export business?'),
  createYesNoQuestion('legal-q9', 'Are you compliant with international sanctions and embargoes affecting your industry?'),
  createLikertQuestion('legal-q10', 'Do you understand and comply with anti-bribery and corruption laws (e.g., FCPA, UK Bribery Act)?'),
  createYesNoQuestion('legal-q11', 'Are your export transactions compliant with your country\'s export control regulations?'),
  createYesNoQuestion('legal-q12', 'Do you maintain proper records to comply with audit and legal inspection requirements?'),
  createLikertQuestion('legal-q13', 'Do your export activities adhere to environmental, health, and safety regulations in target markets?'),
  createYesNoQuestion('legal-q14', 'Have you reviewed the tax and VAT implications of exporting to various countries?'),
  createYesNoQuestion('legal-q15', 'Do your contracts clearly define responsibilities for product liability in export markets?'),
  createYesNoQuestion('legal-q16', 'Do you verify that your overseas buyers and partners are legally authorized entities?'),
  createYesNoQuestion('legal-q17', 'Are you compliant with data protection and privacy laws in international transactions?'),
  createYesNoQuestion('legal-q18', 'Do you screen buyers and intermediaries against international watchlists?'),
  createYesNoQuestion('legal-q19', 'Are your distribution and agency agreements aligned with the laws of the destination countries?'),
  createLikertQuestion('legal-q20', 'Do you periodically review changes in international trade regulations affecting your business?')
];

// Risk Management Questions
const riskQuestions = [
  createYesNoQuestion('risk-q1', 'Do you have a risk management strategy for your export operations?'),
  createYesNoQuestion('risk-q2', 'Have you identified potential political risks in your target export markets?'),
  createLikertQuestion('risk-q3', 'Do you monitor foreign exchange rate fluctuations and their impact on your pricing?'),
  createYesNoQuestion('risk-q4', 'Have you considered taking out export credit insurance to protect against non-payment?'),
  createYesNoQuestion('risk-q5', 'Do you assess the creditworthiness of your international buyers?'),
  createYesNoQuestion('risk-q6', 'Have you accounted for logistics and supply chain disruptions in your export plans?'),
  createLikertQuestion('risk-q7', 'Do you use Incoterms to clarify delivery responsibilities and reduce misunderstandings?'),
  createYesNoQuestion('risk-q8', 'Have you developed a crisis response plan for international incidents affecting trade?'),
  createYesNoQuestion('risk-q9', 'Do you have contracts in place that address dispute resolution with international partners?'),
  createLikertQuestion('risk-q10', 'Do you track changes in regulations and trade policies that may impact your exports?'),
  createLikertQuestion('risk-q11', 'Have you assessed the reputational risks of operating in certain international markets?'),
  createYesNoQuestion('risk-q12', 'Do you conduct regular risk audits on your export operations?'),
  createYesNoQuestion('risk-q13', 'Have you evaluated the insurance needs for transporting goods overseas?'),
  createYesNoQuestion('risk-q14', 'Do you use hedging instruments or forward contracts to manage currency risk?'),
  createYesNoQuestion('risk-q15', 'Do you screen international customers and partners for fraud or compliance risks?'),
  createLikertQuestion('risk-q16', 'Are you aware of cyber risks in international digital transactions and communications?'),
  createYesNoQuestion('risk-q17', 'Do you have contingency plans in case of product recalls in foreign markets?'),
  createYesNoQuestion('risk-q18', 'Do you have proper documentation to defend against customs or regulatory violations?'),
  createLikertQuestion('risk-q19', 'Do you conduct country risk assessments before entering new export markets?'),
  createLikertQuestion('risk-q20', 'Are you prepared to manage risks arising from changes in international trade agreements?')
];

export const testData: Category[] = [
  {
    id: 'business-plan',
    title: 'Export Business Plan',
    description: 'Assess the completeness and viability of your export business plan.',
    icon: 'LayoutPlaneLine',
    questions: businessPlanQuestions
  },
  {
    id: 'finance',
    title: 'Export Finance',
    description: 'Assess your financial readiness for exporting, including capital requirements and payment terms.',
    icon: 'BarChart4',
    questions: financeQuestions
  },
  {
    id: 'documentation',
    title: 'Export Documentation',
    description: 'Evaluate your knowledge and preparedness regarding export documentation requirements.',
    icon: 'FileText',
    questions: documentationQuestions
  },
  {
    id: 'product',
    title: 'Product Readiness',
    description: 'Evaluate if your products are ready for international markets and meet foreign requirements.',
    icon: 'Package',
    questions: productQuestions
  },
  {
    id: 'compliance',
    title: 'Compliance',
    description: 'Evaluate your understanding of export compliance regulations and requirements.',
    icon: 'FileCheck2',
    questions: complianceQuestions
  },
  {
    id: 'market',
    title: 'Market Knowledge',
    description: 'Evaluate your understanding of target export markets and customer needs.',
    icon: 'Globe',
    questions: marketQuestions
  },
  {
    id: 'digital',
    title: 'Digital Readiness & Technology',
    description: 'Assess your digital capabilities and technological preparedness for export operations.',
    icon: 'Smartphone',
    questions: digitalQuestions
  },
  {
    id: 'logistics',
    title: 'Export Logistics',
    description: 'Evaluate your preparedness for managing international shipping and logistics.',
    icon: 'Truck',
    questions: logisticsQuestions
  },
  {
    id: 'legal',
    title: 'Legal & Regulatory Framework',
    description: 'Assess your understanding of international legal requirements and regulatory frameworks.',
    icon: 'Scale',
    questions: legalQuestions
  },
  {
    id: 'risk',
    title: 'Risk Management',
    description: 'Evaluate your risk assessment and management strategies for export operations.',
    icon: 'ShieldAlert',
    questions: riskQuestions
  }
];

// Map icon strings to actual components
export const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, React.ElementType> = {
    BarChart4,
    FileCheck2,
    FileText,
    Globe,
    LayoutPlaneLine,
    Package,
    Smartphone,
    Truck,
    Scale,
    ShieldAlert
  };
  
  return iconMap[iconName] || BarChart4;
};